import type { Organisation } from './organisation';

export type UserType = 'account_admin' | 'account_owner' | 'account_user' | 'super_user';

export interface UserProfile {
  id: string;
  username: string;
  full_name: string;
  email: string;
  telephone_number: string;
  user_type: UserType;
  is_active: boolean;
  organisation: Organisation;
}

export interface UserData {
  username?: string;  // Optional for updates
  password?: string;  // Optional for updates
  full_name: string;
  email: string;
  telephone_number: string;
  user_type: UserType;
  organisation_id: string;
}

// --- Comprehensive Permission Checking Functions ---

// -- General Permissions --
export const canViewDashboard = (profile: UserProfile): boolean => !!profile;
export const canManageOwnAccount = (profile: UserProfile): boolean => !!profile;
export const canViewReports = (profile: UserProfile): boolean => true;

// -- Data and Verification Management --
export const canManageCustomerData = (profile: UserProfile): boolean => true;
export const canImportCustomers = (profile: UserProfile): boolean => true;
export const canExportData = (profile: UserProfile): boolean => true;
export const canPauseResumeVerifications = (profile: UserProfile): boolean => {
  const userType = profile.user_type.toLowerCase();
  return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

// -- User Management --
export const canManageUsers = (profile: UserProfile): boolean => {
  const userType = profile.user_type.toLowerCase();
  return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

// -- Organisation and Credit Management --
export const canManageOwnOrganisation = (profile: UserProfile): boolean => {
    const userType = profile.user_type.toLowerCase();
    return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

export const canPurchaseCredits = (profile: UserProfile): boolean => {
  const userType = profile.user_type.toLowerCase();
  return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

export const canManageIntegrations = (profile: UserProfile): boolean => {
    const userType = profile.user_type.toLowerCase();
    return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

// -- Charity & Organisation Management --

/**
 * Checks if a user can manage the configuration of an existing org-charity link (the "cog" icon).
 * Allowed for admins and owners to manage their own settings.
 */
export const canManageOrgCharityConfig = (profile: UserProfile): boolean => {
    const userType = profile.user_type.toLowerCase();
    return ['super_user', 'account_owner', 'account_admin'].includes(userType);
};

/**
 * Checks if a user can assign a charity to an organisation.
 * Super User only.
 */
export const canAssignCharityToOrg = (profile: UserProfile): boolean => {
    return profile.user_type.toLowerCase() === 'super_user';
};

// -- Super User Only Permissions --

export const canManageAllOrganisations = (profile: UserProfile): boolean => {
  return profile.user_type.toLowerCase() === 'super_user';
};

export const canManageCharities = (profile: UserProfile): boolean => {
  return profile.user_type.toLowerCase() === 'super_user';
};

export const canManageNumberRegistry = (profile: UserProfile): boolean => {
  return profile.user_type.toLowerCase() === 'super_user';
};